import { QueryClient, QueryFunction } from "@tanstack/react-query";

// Global 401 handler — called when any API request gets an unauthorized response.
// AuthContext registers a callback here to trigger session-expired logout.
let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null) {
  unauthorizedHandler = handler;
}

function notifyUnauthorized() {
  if (unauthorizedHandler) {
    unauthorizedHandler();
  }
}

// Global 403 handler — called when any API request gets a forbidden response.
// AuthContext registers a callback here to show a non-intrusive warning toast.
let forbiddenHandler: ((message: string) => void) | null = null;

export function setForbiddenHandler(handler: ((message: string) => void) | null) {
  forbiddenHandler = handler;
}

function notifyForbidden(message: string) {
  if (forbiddenHandler) {
    forbiddenHandler(message);
  }
}

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    let message = res.statusText;
    try {
      const text = await res.text();
      if (text) {
        try {
          const json = JSON.parse(text);
          message = json.message || json.error || message;
        } catch {
          // If not JSON, use text only if it's not HTML
          if (!text.startsWith("<")) {
            message = text;
          }
        }
      }
    } catch {
      // ignore read errors
    }
    throw new Error(`${res.status}: ${message}`);
  }
}

export async function apiRequest(
  methodOrUrl: string,
  urlOrOptions?: string | RequestInit,
  dataOrOptions?: any
): Promise<any> {
  // Handle both signatures: (method, url, data) and (url, options)
  let method: string;
  let url: string;
  let options: RequestInit = {};
  
  if (typeof urlOrOptions === 'string') {
    // Called with (method, url, data) signature
    method = methodOrUrl;
    url = urlOrOptions;
    options = { method };
    if (dataOrOptions) {
      options.body = dataOrOptions;
    }
  } else {
    // Called with (url, options) signature
    url = methodOrUrl;
    options = urlOrOptions || {};
    method = options.method || 'GET';
  }

  const headers: Record<string, string> = {
    ...((options?.headers as Record<string, string>) || {}),
  };

  let body = options?.body;
  
  // If body is an object, stringify it and set content-type
  if (body && typeof body === 'object' && !(body instanceof FormData)) {
    body = JSON.stringify(body, (key, value) => {
      // Convert Date objects to ISO strings for JSON transmission
      if (value instanceof Date) {
        return value.toISOString();
      }
      return value;
    });
    headers['Content-Type'] = 'application/json';
  }

  const res = await fetch(url, {
    credentials: "include",
    ...options,
    headers,
    body,
  });

  if (res.status === 401) {
    notifyUnauthorized();
  }

  if (res.status === 403) {
    notifyForbidden("You don't have permission to view some of this data.");
  }

  await throwIfResNotOk(res);

  // Handle empty responses (e.g. DELETE returning 204 or empty body)
  const contentType = res.headers.get("content-type");
  if (res.status === 204 || !contentType?.includes("application/json")) {
    return null;
  }
  return await res.json();
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey[0] as string, {
      credentials: "include",
    });

    if (res.status === 401) {
      notifyUnauthorized();
      if (unauthorizedBehavior === "returnNull") {
        return null;
      }
    }

    if (res.status === 403) {
      notifyForbidden("You don't have permission to view some of this data.");
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: (failureCount, error) => {
        const status = parseInt((error as Error).message);
        if ([401, 403, 404].includes(status)) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});
